import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaCheckCircle, FaExclamationCircle, FaExclamationTriangle, FaInfoCircle, FaTimes } from "react-icons/fa";
import { supabase } from "../supabaseClient";

const severityMeta = {
  Low: { icon: FaCheckCircle, label: "Low priority notice" },
  Medium: { icon: FaExclamationTriangle, label: "Medium priority notice" },
  High: { icon: FaExclamationCircle, label: "High priority notice" },
  Critical: { icon: FaExclamationCircle, label: "Critical notice" },
};

function audienceMatches(alert, profile) {
  if (!alert?.audience || alert.audience === "Everyone") return true;
  return String(profile?.position || "").toLowerCase() === String(alert.audience).toLowerCase();
}

const URL_PATTERN = /(https?:\/\/[^\s<]+|www\.[^\s<]+|(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<]*)?)/gi;
const TRAILING_URL_PUNCTUATION = /[.,!?;:)}\]>'"]+$/;

function LinkifiedText({ text }) {
  const value = String(text || "");
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  URL_PATTERN.lastIndex = 0;
  while ((match = URL_PATTERN.exec(value)) !== null) {
    if (match.index > lastIndex) parts.push(value.slice(lastIndex, match.index));

    const rawMatch = match[0];
    const trailing = rawMatch.match(TRAILING_URL_PUNCTUATION)?.[0] || "";
    const displayUrl = trailing ? rawMatch.slice(0, -trailing.length) : rawMatch;
    const href = /^https?:\/\//i.test(displayUrl) ? displayUrl : `https://${displayUrl}`;

    if (displayUrl) {
      parts.push(
        <a key={`alert-link-${key++}`} href={href} target="_blank" rel="noopener noreferrer">
          {displayUrl}
        </a>
      );
    }
    if (trailing) parts.push(trailing);
    lastIndex = match.index + rawMatch.length;
  }

  if (lastIndex < value.length) parts.push(value.slice(lastIndex));
  return <>{parts.length ? parts : value}</>;
}

export default function IntranetAlertBars() {
  const [userId, setUserId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());

  const load = useCallback(async () => {
    const { data: authData } = await supabase.auth.getSession();
    const uid = authData?.session?.user?.id || null;
    setUserId(uid);
    if (!uid) { setAlerts([]); setProfile(null); return; }

    const now = new Date().toISOString();
    const [{ data: profileData }, { data: alertData }, { data: dismissalData }] = await Promise.all([
      supabase.from("profiles").select("id,position").eq("id", uid).maybeSingle(),
      supabase.from("site_alert_banners").select("*").eq("active", true).lte("starts_at", now).or(`expires_at.is.null,expires_at.gt.${now}`).order("severity_rank", { ascending: false }).order("created_at", { ascending: false }),
      supabase.from("site_alert_dismissals").select("alert_id").eq("user_id", uid),
    ]);
    setProfile(profileData || null);
    setAlerts(alertData || []);
    setDismissed(new Set((dismissalData || []).map((row) => row.alert_id)));
  }, []);

  useEffect(() => {
    void load();
    const channel = supabase.channel("site-alert-bars")
      .on("postgres_changes", { event: "*", schema: "public", table: "site_alert_banners" }, () => void load())
      .subscribe();
    const onVisibility = () => { if (!document.hidden) void load(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => { document.removeEventListener("visibilitychange", onVisibility); void supabase.removeChannel(channel); };
  }, [load]);

  const visible = useMemo(() => alerts.filter((alert) => audienceMatches(alert, profile) && (!alert.closable || !dismissed.has(alert.id))), [alerts, profile, dismissed]);

  const dismiss = async (alertId) => {
    if (!userId) return;
    setDismissed((current) => new Set([...current, alertId]));
    const { error } = await supabase.from("site_alert_dismissals").upsert({ alert_id: alertId, user_id: userId }, { onConflict: "alert_id,user_id" });
    if (error) {
      setDismissed((current) => { const next = new Set(current); next.delete(alertId); return next; });
      console.warn("Unable to dismiss site alert:", error.message);
    }
  };

  if (!visible.length) return null;
  return (
    <section className="ruht-alert-stack" aria-label="Trust alerts">
      {visible.map((alert) => {
        const severity = severityMeta[alert.severity] || severityMeta.Low;
        const Icon = severity.icon || FaInfoCircle;
        return (
          <div key={alert.id} className={`ruht-site-alert ruht-site-alert--${String(alert.severity || "Low").toLowerCase()}`} role={alert.severity === "High" || alert.severity === "Critical" ? "alert" : "status"}>
            <div className="ruht-site-alert__inner nhsuk-width-container">
              <Icon className="ruht-site-alert__icon" aria-hidden="true" />
              <div className="ruht-site-alert__content">
                <strong>{alert.title}</strong>
                <span><LinkifiedText text={alert.message} /></span>
              </div>
              <span className="nhsuk-u-visually-hidden">{severity.label}</span>
              {alert.closable && <button type="button" className="ruht-site-alert__close" onClick={() => dismiss(alert.id)} aria-label={`Dismiss ${alert.title}`}><FaTimes aria-hidden="true" /></button>}
            </div>
          </div>
        );
      })}
    </section>
  );
}
